<template>
  <div>
    <div class="container-fluid py-2 mt-7">
      <div class="card">
        <div class="row">
          <div class="col-12 text-right">
            <button class="btn btn-link" @click="goBack">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="card-header border-0">
          <div class="row">
            <div class="col-6">
              <h3 class="mb-0">{{ selectedMonth }} {{ selectedYear }}</h3>
            </div>
            <div class="col-6 text-right">
              <el-tooltip content="Export" placement="top">
                <base-button type="primary" icon size="sm" @click="downloadCSV">
                  <span class="btn-inner--icon"><i class="fas fa-file-csv"></i></span>
                  <span class="btn-inner--text">Export</span>
                </base-button>
              </el-tooltip>
            </div>
          </div>
        </div>
        <div v-for="(week, index) in weeklyData" :key="index" class="my-4">
          <h5 class="text-center">Week {{ index + 1 }}</h5>
          <el-table :data="week.tableData" class="table-responsive" border>
            <el-table-column prop="strategyName" label="Strategies" min-width="230px">
              <template #header>
                <div class="header-container font-weight-bolder">Strategies</div>
              </template>
              <template #default="{ row }">
                <div class="text-left">
                  {{ row.strategyName }}
                </div>
              </template>
            </el-table-column>
            <el-table-column v-for="(date, dateIndex) in week.dates" :key="dateIndex" :prop="date" :label="date"
              min-width="160px">
              <template #header>
                <div class="header-container font-weight-bolder">{{ date }}</div>
              </template>
              <template #default="{ row }">
                <div class="p-2" :class="[getTextColor(row[date]), row[date] >= 0 ? 'positive-number' : '']">
                  {{ typeof row[date] === 'number' ? row[date].toFixed(2) : row[date] ?? '--' }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="Total" label="Total" min-width="150px">
              <template #header>
                <div class="header-container font-weight-bolder">Total</div>
              </template>
              <template #default="{ row }">
                <div class="p-2 font-weight-bold" :class="[getTextColor(row.total)]">
                  {{ typeof row.total === 'number' ? row.total.toFixed(2) : '--' }}
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ElTable, ElTableColumn, ElTooltip } from "element-plus";
export default {
  components: {
    [ElTable.name]: ElTable,
    [ElTableColumn.name]: ElTableColumn,
    [ElTooltip.name]: ElTooltip,
  },
  data() {
    return {
      strategyNames: [],
      strategies: [],
      selectedMonth: "",
      selectedYear: "",
      formattedDates: [],
      tableData: {},
      strategyColors: {},
    };
  },
  computed: {
    weeklyData() {
      const weeklyGroups = [];
      let currentWeek = [];
      this.formattedDates.forEach((date, index) => {
        currentWeek.push(date);
        if ((index + 1) % 5 === 0 || index === this.formattedDates.length - 1) {
          weeklyGroups.push([...currentWeek]);
          currentWeek = [];
        }
      });
      return weeklyGroups.map((weekDates) => ({
        dates: weekDates,
        tableData: this.transformDataForWeek(weekDates),
      }));
    },
  },
  mounted() {
    this.selectedMonth = sessionStorage.getItem("MonthSelected");
    this.selectedYear = parseInt(sessionStorage.getItem("SelectedYear")) || new Date().getFullYear();
    this.fetchData();
  },
  methods: {
    goBack() {
      this.$router.push("/strategyDashboard");
    },
    async fetchData() {
      this.generateWeekdayDates();
      await this.fetchStrategyData();
    },
    generateWeekdayDates() {
      this.formattedDates = [];
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth();
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      const selectedMonthIndex = monthNames.indexOf(this.selectedMonth);
      let firstDay = new Date(this.selectedYear, selectedMonthIndex, 1);
      let lastDay = new Date(this.selectedYear, selectedMonthIndex + 1, 0);
      if (this.selectedYear === currentYear && selectedMonthIndex === currentMonth) {
        lastDay = today;
      }
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const currentDate = new Date(this.selectedYear, selectedMonthIndex, day);
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const formattedDate = `${monthNames[selectedMonthIndex].substring(0, 3)} ${String(day).padStart(2, "0")}`;
          this.formattedDates.push(formattedDate);
        }
      }
    },
    async fetchStrategyData() {
      let strategyDataMap = {};
      const apiPromises = this.formattedDates.map((formattedDate) => {
        let [month, day] = formattedDate.split(" ");
        let fullDate = `${this.selectedYear}-${this.getMonthNumber(month)}-${day}`;
        return this.$store
          .dispatch("strategyDashboard/getStrategyData", { fromDate: fullDate, toDate: fullDate }, { root: true })
          .then((response) => {
            const apiData = response.server.data;
            strategyDataMap[formattedDate] = {};
            Object.keys(apiData).forEach((key, index) => {
              strategyDataMap[formattedDate][`strategy${index + 1}`] = apiData[key] === null ? '--' : apiData[key];
            });
            if (!this.strategies.length) {
              this.strategies = Object.keys(apiData).map((key, index) => ({ prop: `strategy${index + 1}` }));
              this.strategyNames = Object.keys(apiData).map((key) => key.replace(/_/g, " "));
            }
          })
          .catch((error) => {
            console.error(`Error fetching data for ${fullDate}:`, error);
          });
      });
      await Promise.all(apiPromises);
      this.tableData = strategyDataMap;
    },
    transformDataForWeek(weekDates) {
      let transformed = [];
      this.strategies.forEach((strategy, index) => {
        let row = { strategyName: this.strategyNames[index] };
        let total = 0;
        weekDates.forEach((date) => {
          const value = this.tableData[date]?.[strategy.prop];
          row[date] = value ?? '--';
          total += typeof value === 'number' ? value : 0;
        });
        row.total = total;
        transformed.push(row);
      });
      return transformed;
    },
    getTextColor(value) {
      if (value > 0) return "text-green";
      if (value < 0) return "text-red";
      return "text-gray";
    },
    getMonthNumber(month) {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const index = months.indexOf(month);
      return index !== -1 ? (index + 1).toString().padStart(2, '0') : "01";
    },
    downloadCSV() {
      let csvContent = "data:text/csv;charset=utf-8,";
      this.weeklyData.forEach((week, weekIndex) => {
        csvContent += `Week ${weekIndex + 1}\n`;
        const headers = ["Strategy", ...week.dates, "Total"];
        csvContent += headers.join(",") + "\n";
        week.tableData.forEach((row) => {
          const rowData = [row.strategyName, ...week.dates.map((date) => typeof row[date] === 'number' ? row[date].toFixed(2) : row[date] ?? "--"), typeof row.total === 'number' ? row.total.toFixed(2) : "--"];
          csvContent += rowData.join(",") + "\n";
        });
        csvContent += "\n";
      });
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "weekly_data.csv");
      document.body.appendChild(link);
      link.click();
    }
  },
};
</script>
